

import { apiListAndData } from "./apiListAndData";
import { API_BASE_URL } from "./config-v4-api";
import { getFromSession } from "./util";

export async function fetchApiData(endpoint, payload = {}, method = 'POST') {
  const token = getFromSession("token");
  // Build full base URL
  let apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/${endpoint.replace(/^\//, '')}`;

  // Append query params for GET
  if (method === 'GET' && Object.keys(payload).length > 0) {
    // const query = new URLSearchParams(payload).toString();
    // apiUrl += `?${query}`;
    const query = new URLSearchParams(payload).toString();
    const separator = apiUrl.includes('?') ? '&' : '?';
    apiUrl += `${separator}${query}`;
  }

  try {
    const response = await fetch(apiUrl, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Token ' + token,
      },
      body: method === 'GET' ? null : JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) throw new Error(data.message || 'Request failed');

    return { isSuccess: true, data };
  } catch (error) {
    return { isSuccess: false, error: error.message };
  }
}

// Retrieves API response data by name from the `apiListAndData` object.
// If the name or data is not found, it returns null.
export function getApiDataByName(name) {
  return apiListAndData?.[name]?.data ?? null;
}

/**
 * Groups room codes by their respective room class.
 * 
 * @param {Array} rooms - List of room objects, each containing `room_class` and `room_code`.
 * @returns {Object} - An object where keys are room classes and values are arrays of room codes.
 */
export function groupRoomTypesByClass(rooms) {
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return {};
  }

  return rooms.reduce((acc, room) => {
    const { room_class, room_code } = room;

    // Only proceed if both class and code are present
    if (room_class && room_code) {
      if (!acc[room_class]) {
        acc[room_class] = [];
      }
      acc[room_class].push(room_code);
    }

    return acc;
  }, {});
}

export function groupRoomTypes(rooms) {
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return [];
  }

  return rooms.reduce((acc, room) => {
    const { room_class, room_code } = room;

    // Only proceed if both class and code are present
    if (room_class && room_code && !acc.includes(room_code)) {
      acc.push(room_code);
    }

    return acc;
  }, []);
}

/**
 * Extracts unique room classes from a list of rooms.
 * 
 * @param {Array} rooms - List of room objects, each potentially containing `room_class`.
 * @returns {Array} - A list of unique room class names.
 */
export function getUniqueRoomClasses(rooms) {
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return [];
  }

  const classSet = new Set();

  rooms.forEach(room => {
    if (room.room_class) {
      classSet.add(room.room_class);
    }
  });

  return Array.from(classSet);
}

/**
 * Retrieves specific room details from a precomputed map of rooms.
 * 
 * @param {Object} roomMap - A mapping of room_code to room details.
 * @param {String} code - The room_code for which details are needed.
 * @returns {Object|null} - An object containing `inventory` and `display_name` if found, otherwise null.
 */
export function getRoomDetailsFromMap(roomMap, code) {
  const room = roomMap?.[code];
  return room
    ? { inventory: room.inventory, display_name: room.display_name }
    : null;
}
